import {
  ChangeDetectionStrategy,
  Component,
  ElementRef,
  OnDestroy,
  OnInit,
  inject,
  input,
  signal
} from '@angular/core';
import { UmssButtonComponent } from '../button/button.component';
import { StickyBottomBarPortalService } from '../../services/sticky-bottom-bar-portal.service';

const STORAGE_KEY = 'umss-feedback-btn-pos';
const HINT_KEY = 'umss-feedback-btn-hint-seen';
/** Stored as 0–100 percentages of viewport so position scales across window sizes */
const DEFAULT_POS = { bottomPct: 3.5, leftPct: 1.5 };
const SAFE_MARGIN = 12;
const DRAG_THRESHOLD_PX = 3;
/** How often to re-check for the sticky-bottom-bar outlet before it registers */
const OUTLET_POLL_INTERVAL_MS = 250;
const OUTLET_POLL_TIMEOUT_MS = 5000;
const MAX_OUTLET_POLL_ATTEMPTS = Math.ceil(
  OUTLET_POLL_TIMEOUT_MS / OUTLET_POLL_INTERVAL_MS
);

@Component({
  selector: 'umss-feedback-button',
  standalone: true,
  imports: [UmssButtonComponent],
  templateUrl: './feedback-button.component.html',
  styleUrl: './feedback-button.component.scss',
  changeDetection: ChangeDetectionStrategy.OnPush,
  host: {
    '[class.dragging]': 'isDragging()',
    '[class.pulsing]': 'isPulsing()'
  }
})
export class UmssFeedbackButtonComponent implements OnInit, OnDestroy {
  readonly feedbackUrl = input.required<string>();

  private readonly el = inject(ElementRef<HTMLElement>);
  private readonly stickyBarPortal = inject(StickyBottomBarPortalService);

  protected isDragging = signal(false);
  protected isPulsing = signal(false);
  private dragMoved = false;
  private startX = 0;
  private startY = 0;
  private startLeft = 0;
  private startBottom = 0;
  private stickyBarHeight = 0;
  private stickyResizeObserver: ResizeObserver | null = null;
  private stickyMutationObserver: MutationObserver | null = null;
  private stickyPollTimeoutId: ReturnType<typeof setTimeout> | null = null;
  private stickyPollAttempts = 0;
  /**
   * True until the user drags the button into a custom position. While true,
   * the button hugs the sticky bars and tracks their height as it changes
   * (e.g. while async page data resolves and bars resize/appear/disappear).
   * Once the user drags, their position is respected and only nudged up to
   * avoid new overlaps — never pulled back down.
   */
  private autoPositioned = true;
  /**
   * Set right before opening feedback from a pointer-driven click (see
   * `onPointerUp`), so the synthetic `click` that follows doesn't trigger
   * `onActivate()` again and open the form a second time.
   */
  private suppressNextActivate = false;

  ngOnInit(): void {
    const saved = this.loadSavedPosition();
    this.autoPositioned = saved === null;

    const pos = saved ?? this.defaultPosition();
    this.applyPosition(pos.left, pos.bottom);

    this.observeStickyBars();

    if (!localStorage.getItem(HINT_KEY)) {
      this.isPulsing.set(true);
      setTimeout(() => {
        this.isPulsing.set(false);
        localStorage.setItem(HINT_KEY, '1');
      }, 5400); // 3 pulses × 1.8s each
    }
  }

  ngOnDestroy(): void {
    this.removeDragListeners();
    this.disconnectStickyObservers();
    if (this.stickyPollTimeoutId !== null) {
      clearTimeout(this.stickyPollTimeoutId);
    }
  }

  /**
   * Keeps the button clear of any docked sticky bottom bars.
   *
   * The `[umssStickyBottomBarOutlet]` element uses `display: contents`, so
   * it has no measurable box — instead we observe its children (the actual
   * sticky bars) directly. The outlet may not be registered yet (e.g. the
   * layout hosting it is lazy-loaded), so retry briefly until it appears.
   */
  private observeStickyBars(): void {
    const outlet = this.stickyBarPortal.getOutlet();
    if (!outlet) {
      if (this.stickyPollAttempts++ < MAX_OUTLET_POLL_ATTEMPTS) {
        this.stickyPollTimeoutId = setTimeout(
          () => this.observeStickyBars(),
          OUTLET_POLL_INTERVAL_MS
        );
      }
      return;
    }

    const update = () => {
      // Sticky bars stack vertically (flex column, flex-shrink: 0), so the
      // button must clear the combined height of all of them, not just one.
      this.stickyBarHeight = Array.from(outlet.children).reduce(
        (sum, child) => sum + child.getBoundingClientRect().height,
        0
      );

      const host = this.el.nativeElement as HTMLElement;
      const minBottom = this.stickyBarHeight + SAFE_MARGIN;
      const left =
        parseFloat(host.style.left) ||
        (DEFAULT_POS.leftPct / 100) * window.innerWidth;

      if (this.autoPositioned) {
        // Hug just above the sticky bars, and drop back to the default resting
        // position when they shrink or disappear — track height both ways.
        this.applyPosition(left, Math.max(this.defaultPosition().bottom, minBottom));
      } else {
        // User has dragged: respect their position, only nudge up to clear a
        // newly-overlapping bar — never pull it back down.
        const currentBottom = parseFloat(host.style.bottom) || 0;
        if (currentBottom < minBottom) {
          this.applyPosition(left, minBottom);
        }
      }
    };

    const observeChildren = () => {
      this.stickyResizeObserver?.disconnect();
      for (const child of Array.from(outlet.children)) {
        this.stickyResizeObserver?.observe(child);
      }
      update();
    };

    this.stickyResizeObserver = new ResizeObserver(update);
    this.stickyMutationObserver = new MutationObserver(observeChildren);
    this.stickyMutationObserver.observe(outlet, { childList: true });
    observeChildren();
  }

  private disconnectStickyObservers(): void {
    this.stickyResizeObserver?.disconnect();
    this.stickyMutationObserver?.disconnect();
  }

  onPointerDown(event: PointerEvent): void {
    if (event.button !== 0) return;
    this.isDragging.set(true);
    this.dragMoved = false;

    const host = this.el.nativeElement as HTMLElement;
    const rect = host.getBoundingClientRect();
    this.startX = event.clientX;
    this.startY = event.clientY;
    this.startLeft = rect.left;
    this.startBottom = window.innerHeight - rect.bottom;

    host.setPointerCapture(event.pointerId);
    host.addEventListener('pointermove', this.onPointerMove);
    host.addEventListener('pointerup', this.onPointerUp);
  }

  private onPointerMove = (event: PointerEvent): void => {
    const dx = event.clientX - this.startX;
    const dy = event.clientY - this.startY;

    if (Math.abs(dx) > DRAG_THRESHOLD_PX || Math.abs(dy) > DRAG_THRESHOLD_PX) {
      this.dragMoved = true;
      // A real drag takes over positioning: stop auto-tracking the sticky bars
      // so a resize mid/post-drag can't yank the button back down.
      this.autoPositioned = false;
    }

    if (!this.dragMoved) return;

    const host = this.el.nativeElement as HTMLElement;
    const newLeft = Math.max(
      0,
      Math.min(this.startLeft + dx, window.innerWidth - host.offsetWidth)
    );
    const newBottom = Math.max(
      this.stickyBarHeight + SAFE_MARGIN,
      Math.min(this.startBottom - dy, window.innerHeight - host.offsetHeight)
    );
    this.applyPosition(newLeft, newBottom);
  };

  private onPointerUp = (event: PointerEvent): void => {
    this.isDragging.set(false);
    this.removeDragListeners();

    const host = this.el.nativeElement as HTMLElement;
    host.releasePointerCapture(event.pointerId);

    if (this.dragMoved) {
      const rect = host.getBoundingClientRect();
      const pct = {
        leftPct: (rect.left / window.innerWidth) * 100,
        bottomPct:
          ((window.innerHeight - rect.bottom) / window.innerHeight) * 100
      };
      try {
        localStorage.setItem(STORAGE_KEY, JSON.stringify(pct));
      } catch {
        // localStorage unavailable (private browsing quota exceeded etc.) — silently ignore
      }
      return;
    }

    // Open immediately on the pointer interaction itself rather than relying
    // on the synthetic `click` that follows — pointer capture can retarget
    // that click away from <umss-button>'s inner <button>, which would
    // prevent its `onClick` output (and therefore onActivate) from firing.
    this.suppressNextActivate = true;
    this.openFeedback();
  };

  /**
   * Triggered by `<umss-button>`'s `onClick` output, which fires for both
   * mouse clicks and keyboard activation (Enter/Space).
   *
   * For pointer-driven clicks, `onPointerUp` already opened the feedback form
   * and set `suppressNextActivate` so the synthetic click that follows isn't
   * acted on again. For keyboard activation (no preceding pointer events),
   * neither flag is set, so this opens the form directly. A drag ends with a
   * synthetic click too, which is swallowed via `dragMoved`.
   */
  onActivate(): void {
    if (this.suppressNextActivate) {
      this.suppressNextActivate = false;
      return;
    }
    if (this.dragMoved) {
      this.dragMoved = false;
      return;
    }
    this.openFeedback();
  }

  /**
   * Returns the user's dragged position from a previous session, or `null`
   * when none is stored (or it's corrupt/out of range). `null` is what tells
   * `ngOnInit` to start in auto-positioned mode.
   */
  private loadSavedPosition(): { left: number; bottom: number } | null {
    try {
      const saved = localStorage.getItem(STORAGE_KEY);
      if (saved) {
        const { leftPct, bottomPct } = JSON.parse(saved);
        if (
          typeof leftPct === 'number' &&
          leftPct >= 0 &&
          leftPct <= 90 &&
          typeof bottomPct === 'number' &&
          bottomPct >= 0 &&
          bottomPct <= 90
        ) {
          return {
            left: (leftPct / 100) * window.innerWidth,
            bottom: (bottomPct / 100) * window.innerHeight
          };
        }
      }
    } catch {
      // corrupted or unavailable — treat as no saved position
    }
    return null;
  }

  private defaultPosition(): { left: number; bottom: number } {
    return {
      left: (DEFAULT_POS.leftPct / 100) * window.innerWidth,
      bottom: (DEFAULT_POS.bottomPct / 100) * window.innerHeight
    };
  }

  private applyPosition(left: number, bottom: number): void {
    const host = this.el.nativeElement as HTMLElement;
    host.style.left = `${left}px`;
    host.style.bottom = `${bottom}px`;
  }

  private removeDragListeners(): void {
    const host = this.el.nativeElement as HTMLElement;
    host.removeEventListener('pointermove', this.onPointerMove);
    host.removeEventListener('pointerup', this.onPointerUp);
  }

  openFeedback(): void {
    window.open(this.feedbackUrl(), '_blank', 'noopener,noreferrer');
  }
}
